import { Injectable, NgZone } from '@angular/core';
import { AlertController } from '@ionic/angular';
import { BackendError } from '../types/BackendError';
import { words } from 'lodash';
import { AlertButton, IonicSafeString, OverlayEventDetail } from '@ionic/core';

@Injectable({
  providedIn: 'root'
})
export class AlertService {

  brand: any;
  constructor(public alertCtrl: AlertController, private ngZone: NgZone) { }

  showEmptyForm() {
    this.showSmall('Check the form', 'Please fill in the form');
  }

  showOfflineAlert(unavailableAction: string) {
    this.showSmall(
      `You appear to be offline`,
      `Sorry we can't ${unavailableAction} while you are offline.`
    );
  }

  showError(error: any): void {
    if (error?.message === 'form_empty') {
      return this.showEmptyForm();
    }

    let header = 'Error';
    let message = 'An unexpected error occurred';

    if (error?.statusText) {
      header = error.statusText;
    } else if (error?.name) {
      header = error.name.replace(/Error$/, '').replace(/([A-Z])/g, ' $1').trim();
    }

    if (error?.message) {
      message = error.message;
    } else if (typeof error === 'string') {
      message = error;
    } else if (error?.error?.message) {
      message = error.error.message;
    }

    return this.show(header, message);
  }

  showAmplifyAuthError(error: any): void {
    const code = words(error.code).join(' ');
    return this.show(`${code}`, `${error.message}`);
  }

  show(
    header: string,
    message: string,
    onDidDismiss?: (data: any, role: string) => void,
    buttons?: (AlertButton | string)[]
  ): void {
    this.alertCtrl.create({
      header,
      message,
      buttons: buttons ? buttons : ['OK']
    }).then(async alertEl => {
      if (onDidDismiss) {
        alertEl.onDidDismiss().then((value: OverlayEventDetail<any>) => {
          onDidDismiss(value.data, value.role!);
        });
      }
      await alertEl.present();
    });
  }

  showSmall(
    header: string,
    message: string | IonicSafeString,
    onDidDismiss?: (data: any, role: string) => void,
    buttons?: (AlertButton | string)[],
    backdropDismiss?: boolean,
    cssClass?: string
  ): void {
    const alert = this.alertCtrl.create({
      header,
      message,
      buttons: buttons ? buttons : ['OK'],
      backdropDismiss,
      cssClass
    }).then(async alertEl => {
      alertEl.onDidDismiss().then(overlayEvent => {
        if (onDidDismiss) {
          onDidDismiss(overlayEvent.data, overlayEvent.role!);
        }
      });
      await alertEl.present();
    });
  }

  confirm(
    title: string = 'Confirm',
    message: string = 'Are you sure?',
    confirmText?: string,
    confirmCallback?: () => void,
    cancelText?: string,
    cancelCallback?: () => void
  ) {
    this.showSmall(title, message, undefined, [
      {
        text: cancelText || 'Cancel',
        role: 'cancel',
        handler: () => this.ngZone.run(() => {
          cancelCallback && cancelCallback();
        })
      },
      {
        text: confirmText || 'OK',
        handler: () => this.ngZone.run(() => {
          confirmCallback && confirmCallback();
        })
      }
    ]);
  }

  confirmDiscard(
    title: string = 'Discard',
    message: string = 'Are you sure you want to discard?',
    confirmText?: string,
    confirmCallback?: () => void,
    cancelText?: string,
    cancelCallback?: () => void
  ) {
    this.showSmall(title, message, undefined, [
      {
        text: cancelText || 'Close',
        role: 'cancel',
        handler: () => this.ngZone.run(() => {
          cancelCallback && cancelCallback();
        })
      },
      {
        text: confirmText || 'Discard',
        handler: () => this.ngZone.run(() => {
          confirmCallback && confirmCallback();
        })
      }
    ]);
  }
}
